import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Check,
  AlertCircle,
  Loader2,
  X,
  ExternalLink,
} from 'lucide-react';
import { useProcessing } from '../../contexts/ProcessingContext';

export function ProcessingProgressToast() {
  const navigate = useNavigate();
  const { activeProcessing, showToast, dismissToast } = useProcessing();

  if (!showToast || !activeProcessing) return null;

  const status = activeProcessing.status;
  const progress = status?.progress;
  const result = status?.result;
  const percentage = progress?.progress || (result ? 100 : 0);
  const processed = progress?.processed || result?.processed || 0;
  const total = progress?.total || result?.total || activeProcessing.imageCount;

  const isComplete = status?.status === 'SUCCESS';
  const isFailed = status?.status === 'FAILURE';
  const isPending = !status || status?.status === 'PENDING';
  const isCancelled = isComplete && result?.was_cancelled;

  const handleClick = () => {
    navigate(`/batch-history/${activeProcessing.batchId}`);
    dismissToast();
  };

  const getStatusColor = () => {
    if (isCancelled) return 'from-amber-500 to-yellow-500';
    if (isComplete) return 'from-emerald-500 to-green-500';
    if (isFailed) return 'from-red-500 to-orange-500';
    return 'from-primary to-purple-500';
  };

  const getStatusBg = () => {
    if (isCancelled) return 'bg-amber-500/10 border-amber-500/30';
    if (isComplete) return 'bg-emerald-500/10 border-emerald-500/30';
    if (isFailed) return 'bg-red-500/10 border-red-500/30';
    return 'bg-primary/10 border-primary/30';
  };

  const getIcon = () => {
    if (isCancelled) return <X className="w-5 h-5 text-amber-500" />;
    if (isComplete) return <Check className="w-5 h-5 text-emerald-500" />;
    if (isFailed) return <AlertCircle className="w-5 h-5 text-red-500" />;
    if (isPending) return <Loader2 className="w-5 h-5 text-primary animate-spin" />;
    return <Sparkles className="w-5 h-5 text-primary animate-pulse" />;
  };

  const getMessage = () => {
    if (isCancelled) return 'Processing cancelled';
    if (isComplete) return 'Processing complete';
    if (isFailed) return 'Processing failed';
    if (isPending) return 'Starting...';
    return `Processing ${processed}/${total}`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div
        className={`relative flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-sm cursor-pointer transition-all hover:scale-[1.02] ${getStatusBg()}`}
        onClick={handleClick}
      >
        {/* Dismiss button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismissToast();
          }}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-muted border border-border flex items-center justify-center hover:bg-muted/80 transition-colors"
        >
          <X className="w-3 h-3 text-muted-foreground" />
        </button>

        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isCancelled ? 'bg-amber-500/20' :
          isComplete ? 'bg-emerald-500/20' :
          isFailed ? 'bg-red-500/20' :
          'bg-primary/20'
        }`}>
          {getIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{getMessage()}</span>
            <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          </div>

          {/* Progress bar */}
          {!isComplete && !isFailed && (
            <div className="mt-1.5 w-48">
              <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out bg-gradient-to-r ${getStatusColor()}`}
                  style={{ width: `${percentage}%` }}
                />
                {/* Shimmer */}
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
                  style={{ backgroundSize: '200% 100%' }}
                />
              </div>
            </div>
          )}

          {/* Percentage for in-progress */}
          {!isComplete && !isFailed && !isPending && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {Math.round(percentage)}% complete
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
}
